import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { CopyField } from '../ui/CopyField';
import { Countdown } from '../ui/Countdown';
import { Button } from '../ui/Button';
import { LinkButton } from '../ui/LinkButton';
import { EXPIRY_SECONDS } from '../../lib/api';

export default function DisplaySecretLink() {
  const { id } = useParams<{ id: string }>();
  const [expired, setExpired] = useState(false);

  const linkUrl = `${window.location.protocol}//${window.location.host}/s/${id}`;
  const expiresAt = useMemo(() => Date.now() + EXPIRY_SECONDS * 1000, []);

  const notifyCopied = () => toast.success('Link copied to clipboard');

  const copyAndNotify = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      notifyCopied();
    } catch {
      toast.error("Couldn't access your clipboard");
    }
  };

  if (expired) {
    return (
      <Card>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text">
          Link expired
        </h1>
        <p className="mt-3 text-sm text-text-muted">
          This link is no longer valid. Secrets vanish after one read or 10 minutes.
        </p>
        <div className="mt-6">
          <LinkButton to="/" fullWidth leadingIcon={<Sparkles size={18} />}>
            Send a new secret
          </LinkButton>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text sm:text-[2rem]">
          Your link is ready
        </h1>
        <Countdown expiresAt={expiresAt} onExpire={() => setExpired(true)} />
      </div>
      <p className="mt-3 text-sm text-text-muted sm:text-base">
        Share this link. It self-destructs after the first read.
      </p>
      <div className="mt-6">
        <CopyField value={linkUrl} label="One-time link" onCopy={notifyCopied} />
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          onClick={copyAndNotify}
          leadingIcon={<Copy size={18} />}
          className="w-full sm:flex-1"
        >
          Copy link
        </Button>
        <LinkButton to="/" variant="ghost" size="md" className="w-full sm:w-auto whitespace-nowrap">
          Send another
        </LinkButton>
      </div>
    </Card>
  );
}
